import mixpanel from "mixpanel-browser";
import { config } from "@/config";
import { useAuth } from "./use-auth";
import { useCallback } from "react";
import { User } from "@/types/auth";

// Hook-based analytics functions for use within React components
export const useAnalytics = () => {
  const { getAccessToken } = useAuth();

  // Internal helper method for API requests
  const request = useCallback(
    async (endpoint: string, options: RequestInit = {}) => {
      try {
        const token = await getAccessToken();
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
  
        await fetch(`${config.apiUrl}${endpoint}`, {
          ...options,
          headers,
        });  
      }
      catch (error) {
        // Silently fail analytics requests - they shouldn't block the main app
        console.warn("Failed to call analytics endpoint:", error);
      }
    },
    [getAccessToken]
  );

  const identifyUser = useCallback(
    async (user: User) => {
      // Call new analytics identify endpoint
      try {
        await request("/analytics/identify", {
          method: "POST",
          body: JSON.stringify({
            userId: user.id,
            email: user.email,
            name: user.name,
            nickname: user.nickname
          }),
        });
      } catch (error) {
        console.warn("Failed to call analytics identify endpoint:", error);
      }

      // Keep existing Mixpanel identify call
      if (config.mixpanel.token) {
        mixpanel.identify(user.id);
        mixpanel.people.set({
          $email: user.email,
          $name: user.name,
          $nickname: user.nickname,
        });
      }
    },
    [request]
  );

  const trackEvent = useCallback(
    async (
      event: string,
      properties: Record<string, string | number | boolean | Date>
    ) => {
      // Call the analytics track endpoint
      try {
        await request("/analytics/track", {
          method: "POST",
          body: JSON.stringify({
            event,
            properties,
          }),
        });
      } catch (error) {
        console.warn("Failed to call analytics track endpoint:", error);
      }
    },
    [request]
  );

  return {
    identifyUser,
    trackEvent,
  };
};
